import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, useMediaQuery } from '@mui/material';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useAtomValue } from 'jotai';
import useTheme from '@/theme/useTheme';
import ModeToggleButtons from './ModeToggleButtons';
import { LoginCardProps } from './types';
import TermsAndConditions from '@/components/features/common/TermsAndConditions';
import { isLoggedIn } from '@/state/userInfo';
import useResponsiveUI from '@/hooks/useResponsiveUI';

const LoginCard: React.FC<LoginCardProps> = ({
  children,
  mode = 'login',
  onModeChange,
  showModeToggle = true,
  redirectPath = '/games/fomo/zones',
  disableRedirect = false,
  onRedirect,
}) => {
  const { theme } = useTheme();
  const { isDesktop } = useResponsiveUI();
  // const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const [imageLoaded, setImageLoaded] = useState(false);
  const router = useRouter();

  // 获取用户登录状态
  const isLogin = useAtomValue(isLoggedIn);

  const bannerImage = '/assets/images/fomo-box/register_login_banner.png';
  const bannerMobileImage = '/assets/images/fomo-box/login_banner_m.png';

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  // 用户登录状态检查和重定向逻辑
  useEffect(() => {
    // 如果禁用重定向，则不执行重定向
    if (disableRedirect) {
      return;
    }

    // 如果用户已登录，执行重定向
    if (isLogin) {
      // 执行重定向前的回调
      if (onRedirect) {
        onRedirect();
      }

      // 重定向到指定页面
      router.push(redirectPath);
    }
  }, [isLogin, router, redirectPath, disableRedirect, onRedirect]);

  // 如果用户已登录且未禁用重定向，不渲染组件内容（避免闪烁）
  if (!disableRedirect && isLogin) {
    return null;
  }

  return (
    <Card
      elevation={3}
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column-reverse', md: 'row' },
        height: '100%',
        minHeight: { xs: 'auto', md: '420px' },
        overflow: 'hidden',
        zIndex: 1,
        backgroundColor: theme.palette.grey['1100'],
        borderRadius: { xs: 0, md: 1.5 },
        width: { xs: '100%', md: '792px' },
        maxWidth: { xs: '100%', md: '792px' },
      }}
    >
      {/* 左侧表单区域 */}
      <CardContent
        sx={{
          flex: 1,
          zIndex: 2,
          padding: {
            xs: theme.spacing(3, 2),
            sm: theme.spacing(4, 3),
            md: theme.spacing(4, 4),
          },
          width: '100%',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'center',
          borderTop: {
            xs: `1px solid ${theme.palette.divider}`,
            md: 'none',
          },
          '&:last-child': {
            paddingBottom: { xs: theme.spacing(3), sm: theme.spacing(4) },
          },
        }}
      >
        {/* 模式切换按钮 */}
        {showModeToggle && (
          <Box sx={{ width: '100%' }}>
            <ModeToggleButtons currentMode={mode} onModeChange={onModeChange} />
          </Box>
        )}

        {/* 表单内容区域 */}
        <Box
          sx={{
            width: '100%',
            maxWidth: '400px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {children}
          <Box sx={{ mt: 2 }}>
            <TermsAndConditions
              component={'p'}
              sx={{
                fontSize: {
                  xs: theme.typography.pxToRem(11),
                  sm: theme.typography.pxToRem(12),
                },
              }}
            />
          </Box>
        </Box>
      </CardContent>

      {/* 右侧 Banner 区域 */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: { xs: '180px', md: 'auto' },
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          p: { xs: 0, md: 0 },
          backgroundColor: theme.palette.grey['900'],
        }}
      >
        {/* Banner 背景图 */}
        <Image
          src={isDesktop ? bannerImage : bannerMobileImage}
          alt="Login Register Banner"
          fill
          style={{
            objectFit: 'cover',
            objectPosition: 'top',
            zIndex: 1,
            opacity: imageLoaded ? 1 : 0,
            transition: 'opacity .6s ease-in-out',
          }}
          loading="lazy"
          onLoad={handleImageLoad}
          onError={() => {
            setImageLoaded(true);
          }}
        />
      </Box>
    </Card>
  );
};

export default LoginCard;
